'use client';
import React, { useState, useMemo } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/utils/classnames';
import { queryString } from '@/utils/tools';
import type { CountryCode } from '@/features/auth/components/modal-select-country/types';
import { useIpCountry } from '@/features/auth/hooks/use-ip-country';
import ModalText from '../login-form/components/modal-text';
// import iconAdress from '@/features/auth/images/icon/icon-adress.png';
import iconInviteCode from '@/features/auth/images/icon/icon-invite-code.png';
import { Drawer, DrawerContent, DrawerHeader, DrawerOverlay, DrawerTitle } from '@/components/ui/drawer';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { validateInviteCode } from '@/features/auth/utils/validation';
import '../login-form/form.css';
interface ModalRegisterProps {
  /** 登录类型 */
  loginType?: string;
  /** 弹窗标题 */
  title?: string;
  /** 自定义样式 */
  className?: string;
  /** 关闭弹窗时的回调函数 */
  onClose?: () => void;
}

// 定义组合类型 CountryCode + inviteCode
type CountryCodeWithInviteCode = CountryCode & {
  inviteCode?: string;
};

const ModalRegisterInfo = NiceModal.create<ModalRegisterProps>(({
  title = '',
  className,
  onClose
}) => {
  const t = useTranslations('auth');
  const modal = useModal();
  // const [selectedCountry, setSelectedCountry] = useState<CountryCode | null>(null);

  // 从URL获取邀请码默认值
  const inviteCodeFromUrl = queryString<{ c: string; }>()?.c;
  const [inviteCode, setInviteCode] = useState<string>(inviteCodeFromUrl || '');
  // const [isLoading, setIsLoading] = useState(false);
  const [showInviteCodeError, setShowInviteCodeError] = useState(false);
  const { selectedCountry } = useIpCountry();

  // 1. 获取当前选中的国家, 有选中的国家就用选中的国家
  // 2. 取url的语言
  // 3. 没有就用ip获取的国家
  const currentCountry = useMemo(() => {
    return selectedCountry || null;
  }, [selectedCountry]);

  // 检查所选地区与IP地址是否不一致
  // const isCountryMismatch = useMemo(() => {
  //   if (!selectedCountry || !ipCountry) return false;
  //   return selectedCountry.code !== ipCountry.code;
  // }, [selectedCountry, ipCountry]);

  // const handleCountrySelect = () => {
  //   setIsLoading(true);
  //   try {
  //     showModalSelectCountry({
  //       countryCode: (currentCountry as CountryCode)?.code,
  //       showMobileCode: true,
  //       title: t('countryRegionSelection.residenceSelection'),
  //     }).then((country) => {
  //       if (country) {
  //         setSelectedCountry(country as CountryCode);
  //       }
  //     });
  //   } catch (error) {
  //     console.error('选择国家失败:', error);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleInviteCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInviteCode(value);

    // 如果用户输入了内容，则显示验证结果
    if (value.length > 0) {
      setShowInviteCodeError(!validateInviteCode(value).isValid);
    } else {
      setShowInviteCodeError(false);
    }
  };

  const handleConfirm = () => {
    if (currentCountry) {
      modal.resolve({
        ...currentCountry,
        inviteCode: inviteCode,
      } as CountryCodeWithInviteCode);
      modal.hide();
    }
  };

  const handleClose = () => {
    // 调用用户提供的关闭回调
    onClose?.();
    // 返回当前国家信息和邀请码（如果有的话）
    if (currentCountry) {
      modal.resolve({
        ...currentCountry,
        inviteCode: inviteCode,
      } as CountryCodeWithInviteCode);
    } else {
      // 如果没有国家信息，则返回 undefined
      modal.resolve(undefined);
    }
    modal.hide();
  };

  const handleAgreement = () => {
    NiceModal.show(ModalText, { title: t('register.agreement1') });
  };

  return (
    <Drawer
      open={modal.visible}
      onOpenChange={(open) => !open && handleClose()}
      onClose={handleClose}
    >
      <DrawerOverlay
        onPointerDown={(e) => e.preventDefault()}
      />
      <DrawerContent className={cn(
        "w-full bg-surface gap-0 border-none rounded-[10px] text-white px-[27px] pb-[19px] pt-[20px]",
        className
      )}>
        <DrawerHeader className="text-center py-0">
          <DrawerTitle className="text-[16px] leading-[12px] font-semibold text-white">
            {title || t('countryRegionSelection.residenceSelection')}
          </DrawerTitle>
        </DrawerHeader>

        <div>
          {/* <div className="mt-[32px] mb-[25px]">
            <button
              className={cn(
                "w-full flex items-center justify-between comInputDisabled focus-visible:outline-none",
              )}
            >
              <div className="flex items-center">
                <Image  src={iconAdress} alt="iconAdress" className="w-[15px] h-[15px]" width={17} height={17} />
                <span className="text-[13px] pl-[7px]">
                  {currentCountry?.en || ''}
                </span>
              </div>
            </button>
          </div> */}

          <div className="mt-[32px]">
            <div className={cn(
              "relative flex items-center flex-1")}>
              <Image src={iconInviteCode} alt="iconAdress" className="absolute left-[11px]" width={17} height={17} />
              <input
                type="text"
                value={inviteCode}
                onChange={handleInviteCodeChange}
                placeholder={t('register.invitationCode')}
                className="flex-1 comInput !pl-[32px]"
              />
            </div>
            {showInviteCodeError && (
              <div className="errorText">
                {t('errors.invalidInvitationCode')}
              </div>
            )}
          </div>

          <Button
            variant="primary"
            className="w-full mt-[35px]"
            disabled={!currentCountry || (inviteCode.length > 0 && !validateInviteCode(inviteCode).isValid)}
            onClick={handleConfirm}
          >
            {t('passwordReset.confirm')}
          </Button>
          {/* 用户协议 */}
          <div className="pt-[6px] text-center">
            <p className="text-[11px] text-tertiary" onClick={handleAgreement}>
              {t('register.agreement')}
              <span className="text-[11px] leading-[20px] text-secondary pointer pl-[2px]">
                {t('register.agreement1')}
              </span>
            </p>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
});
ModalRegisterInfo.displayName = 'ModalRegisterInfo';
export const showModalRegisterInfo = (props?: ModalRegisterProps): Promise<CountryCodeWithInviteCode | undefined> => {
  return NiceModal.show(ModalRegisterInfo, props);
};

export default ModalRegisterInfo;
